import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  applyThemeToDOM,
  getEffectiveTheme,
  getStoredTheme,
  storeTheme,
  type Theme,
} from '../lib/theme'

interface ThemeContextValue {
  theme: Theme
  effectiveTheme: 'dark' | 'light'
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext)
  if (!value) {
    throw new Error('useTheme must be used inside ThemeProvider')
  }
  return value
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())
  const [effectiveTheme, setEffectiveTheme] = useState<'dark' | 'light'>(() => getEffectiveTheme(getStoredTheme()))

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    storeTheme(next)
    const effective = getEffectiveTheme(next)
    setEffectiveTheme(effective)
    applyThemeToDOM(effective)
  }, [])

  useEffect(() => {
    const effective = getEffectiveTheme(theme)
    setEffectiveTheme(effective)
    applyThemeToDOM(effective)
  }, [theme])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const effective = getEffectiveTheme('system')
      setEffectiveTheme(effective)
      applyThemeToDOM(effective)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
